import { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { IconPin, IconCalendar, IconArrowRight, IconClose } from '../components/Icons';
import useScrollReveal from '../hooks/useScrollReveal';
import './Speaking.css';

/**
 * Helper to extract YouTube video ID from URL
 */
function getYouTubeId(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

function SpeakingCard({ event, onClick, index }) {
  const videoId = getYouTubeId(event.youtubeUrl);
  const thumbnail = event.thumbnailUrl
    ? event.thumbnailUrl
    : (videoId 
      ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` 
      : (event.images && event.images.length > 0 ? event.images[0] : '/placeholder-speaking.jpg')
    );

  return (
    <motion.article 
      className={`speaking-card reveal reveal-up delay-${(index % 3) + 1}`}
      onClick={() => onClick(event)}
    >
      <div className="speaking-card__thumb">
        <img src={thumbnail} alt={event.title} className="speaking-card__img" loading="lazy" />
        {videoId && (
          <div className="speaking-card__play-overlay">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
      </div>
      <div className="speaking-card__content">
        <div className="speaking-card__meta">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconPin size={14} /> {event.location}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconCalendar size={14} /> {event.date}</span>
        </div>
        <h3 className="speaking-card__title">{event.title}</h3>
        <div className="speaking-card__footer">
          <span className="speaking-card__more">View Details</span>
          <IconArrowRight size={16} color="var(--gold)" />
        </div>
      </div>
    </motion.article>
  );
}

function EventModal({ event, onClose }) {
  const videoId = getYouTubeId(event.youtubeUrl);

  return (
    <motion.div 
      className="speaking-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="speaking-modal"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="speaking-modal__close" onClick={onClose} aria-label="Close modal">
          <IconClose size={24} />
        </button>

        {videoId ? (
          <div className="speaking-modal__video-wrap">
            <iframe 
              width="100%" 
              height="100%" 
              src={`https://www.youtube.com/embed/${videoId}`}
              title={event.title}
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            ></iframe>
          </div>
        ) : event.images && event.images.length > 0 && (
          <div className="speaking-modal__video-wrap">
             <img src={event.images[0]} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}

        <div className="speaking-modal__body">
          <header className="speaking-modal__header">
            <div className="speaking-card__meta" style={{ fontSize: '1rem', marginBottom: '1rem' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><IconPin size={18} /> {event.location}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><IconCalendar size={18} /> {event.date}</span>
            </div>
            <h2 className="speaking-modal__title">{event.title}</h2>
          </header>

          <p className="speaking-modal__description">{event.description}</p>

          {event.images && event.images.length > 0 && (
            <div className="speaking-modal__gallery-section">
              <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--navy)' }}>Event Gallery</h4>
              <div className="speaking-gallery">
                {event.images.map((img, idx) => (
                  <div key={idx} className="speaking-gallery__img-wrap">
                    <img src={img} alt={`${event.title} gallery ${idx}`} className="speaking-gallery__img" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Speaking() {
  useScrollReveal();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetch('/api/speaking')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then(data => {
        let loadedEvents = [];
        if (data && data.items) {
          loadedEvents = data.items;
        } else if (Array.isArray(data)) {
          loadedEvents = data;
        }

        const parseEventDate = (evt) => {
          if (!evt || !evt.date) return 0;
          const dateStr = String(evt.date).trim();
          const parsed = Date.parse(dateStr);
          if (!isNaN(parsed)) return parsed;
          const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            const year = parseInt(yearMatch[0], 10);
            const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const lower = dateStr.toLowerCase();
            for (let i = 0; i < 12; i++) {
              if (lower.includes(months[i])) {
                return new Date(year, i, 1).getTime();
              }
            }
            return new Date(year, 0, 1).getTime();
          }
          return evt.id || 0;
        };

        loadedEvents.sort((a, b) => parseEventDate(b) - parseEventDate(a));
        setEvents(loadedEvents);
        setLoading(false);
      })
      .catch(err => {
        console.error("Fetch speaking data error:", err);
        setEvents([]);
        setLoading(false);
      });
  }, []);

  return (
    <main className="speaking-page">
      <section className="page-hero" aria-labelledby="speaking-heading">
        <div className="container reveal reveal-up">
          <span className="section-label">Global Dialogue</span>
          <h1 id="speaking-heading">International Speaking Engagements & Conferences</h1>
          <p className="section-subtitle">
            Advancing interfaith ethics, environmental stewardship, and human rights through diplomatic and academic forums worldwide.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light)', fontStyle: 'italic' }}>Loading engagements…</div>
          ) : (
            <div className="speaking-grid">
              {events.map((event, idx) => (
                <SpeakingCard 
                  key={event.id} 
                  event={event} 
                  index={idx} 
                  onClick={setSelectedEvent} 
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selectedEvent && (
          <EventModal 
            event={selectedEvent} 
            onClose={() => setSelectedEvent(null)} 
          />
        )}
      </AnimatePresence>
    </main>
  );
}
